import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { organizationService } from '../services/api/organizationService';
import { useRBACStore, UserRole } from '../stores/rbac-store';
import { Org } from '../types/dynamodb-tables';

/**
 * Unified Organization Query System
 * 
 * This module provides a consolidated approach to organization data fetching with:
 * - Unified query key factory for consistent caching
 * - Role-based query optimization for security
 * - Intelligent caching strategies with configurable TTL
 * - Support for both individual and search operations
 * - RBAC-aware data fetching based on user permissions
 * 
 * Key Features:
 * - Centralized query key management prevents cache inconsistencies
 * - Role-based caching for security and performance
 * - Optimized garbage collection and stale time settings
 * - Support for prefetching and background updates
 * - Integration with RBAC system for permission-based data access
 */

interface SearchParams extends Record<string, unknown> {
  name?: string;
  noChamps?: boolean;
  parentOrgId?: string;
}

// Unified query key factory for consistent organization caching across the app
// Prevents cache key conflicts and ensures proper invalidation strategies
export const unifiedOrgKeys = {
  all: ['unified-organizations'] as const,
  byId: (orgId: string) => [...unifiedOrgKeys.all, 'byId', orgId] as const,
  byIdWithRole: (orgId: string, role: UserRole | null) => [...unifiedOrgKeys.all, 'byId', orgId, { role }] as const,
  search: {
    districts: (params: Record<string, unknown> = {}) => [...unifiedOrgKeys.all, 'search', 'districts', params] as const,
    schools: (params: Record<string, unknown> = {}) => [...unifiedOrgKeys.all, 'search', 'schools', params] as const,
  },
  rbac: {
    districts: (role: UserRole | null) => [...unifiedOrgKeys.all, 'rbac', 'districts', { role }] as const,
    schools: (role: UserRole | null, parentOrgId?: string | null) => [...unifiedOrgKeys.all, 'rbac', 'schools', { role, parentOrgId }] as const,
  },
};

// Primary hook for fetching individual organizations with role-based caching
export const useUnifiedOrganization = (
  orgId: string,
  options: Omit<UseQueryOptions<Org, Error>, 'queryKey' | 'queryFn'> & {
    includeRole?: boolean;
  } = {}
) => {
  const { getUserRole } = useRBACStore();
  const role = getUserRole();
  const { includeRole = false, ...queryOptions } = options;

  return useQuery<Org, Error>({
    queryKey: includeRole ? unifiedOrgKeys.byIdWithRole(orgId, role) : unifiedOrgKeys.byId(orgId),
    queryFn: () => organizationService.getOrgById(orgId),
    enabled: !!orgId && queryOptions.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes - consistent across all organization queries
    gcTime: 30 * 60 * 1000, // 30 minutes garbage collection
    ...queryOptions,
  });
};

// Hook for getting multiple organizations by IDs (batched)
export const useUnifiedOrganizations = (
  orgIds: string[],
  options: Omit<UseQueryOptions<Org[], Error>, 'queryKey' | 'queryFn'> = {}
) => {
  return useQuery<Org[], Error>({
    queryKey: [...unifiedOrgKeys.all, 'batch', orgIds.sort()],
    queryFn: async () => {
      // Fetch all organizations in parallel
      const promises = orgIds.map(id => organizationService.getOrgById(id));
      const results = await Promise.all(promises);
      return results;
    },
    enabled: orgIds.length > 0 && options.enabled !== false,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    ...options,
  });
};

// Helper hook to get organization with RBAC context
export const useRBACOrganization = (
  orgId: string,
  options: Omit<UseQueryOptions<Org, Error>, 'queryKey' | 'queryFn'> = {}
) => {
  return useUnifiedOrganization(orgId, {
    ...options,
    includeRole: true,
  });
};

// District-specific hook (maintains backward compatibility)
export const useUnifiedDistrict = (
  districtId: string,
  options: Omit<UseQueryOptions<Org, Error>, 'queryKey' | 'queryFn'> = {}
) => {
  return useUnifiedOrganization(districtId, options);
};

// School-specific hook (maintains backward compatibility)
export const useUnifiedSchool = (
  schoolId: string,
  options: Omit<UseQueryOptions<Org, Error>, 'queryKey' | 'queryFn'> = {}
) => {
  return useUnifiedOrganization(schoolId, options);
};

// Hook for districts search (consolidated)
export const useUnifiedDistrictsSearch = (
  params: Record<string, unknown> = {},
  options: Omit<UseQueryOptions<Org[], Error>, 'queryKey' | 'queryFn'> = {}
) => {
  return useQuery<Org[], Error>({
    queryKey: unifiedOrgKeys.search.districts(params),
    queryFn: () => organizationService.searchDistrictOrgs(params as SearchParams),
    enabled: options.enabled !== false,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    ...options,
  });
};

// Hook for schools search (consolidated)
export const useUnifiedSchoolsSearch = (
  params: Record<string, unknown> = {},
  options: Omit<UseQueryOptions<Org[], Error>, 'queryKey' | 'queryFn'> = {}
) => {
  return useQuery<Org[], Error>({
    queryKey: unifiedOrgKeys.search.schools(params),
    queryFn: () => organizationService.searchSchoolOrgs(params as SearchParams),
    enabled: !!params.parentOrgId && options.enabled !== false,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    ...options,
  });
};

// Hook for RBAC-aware districts
export const useUnifiedRBACDistricts = (
  options: Omit<UseQueryOptions<Org[], Error>, 'queryKey' | 'queryFn'> = {}
) => {
  const { getUserRole, getUserOrgId, getUserParentOrgId } = useRBACStore();
  const userRole = getUserRole();
  const userOrgId = getUserOrgId();
  const userParentOrgId = getUserParentOrgId();

  return useQuery<Org[], Error>({
    queryKey: unifiedOrgKeys.rbac.districts(userRole),
    queryFn: async () => {
      let districts: Org[] = [];

      switch (userRole) {
        case 'tm_admin':
          districts = await organizationService.searchDistrictOrgs({ noChamps: true });
          break;

        case 'district_champion':
          if (userOrgId) {
            const district = await organizationService.getOrgById(userOrgId);
            districts = [district];
          }
          break;

        case 'school_champion':
          if (userParentOrgId) {
            const district = await organizationService.getOrgById(userParentOrgId);
            districts = [district];
          }
          break;

        default:
          districts = [];
      }
      return districts;
    },
    enabled: !!userRole && options.enabled !== false,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    ...options,
  });
};

// Hook for RBAC-aware schools
export const useUnifiedRBACSchools = (
  selectedDistrictId?: string,
  options: Omit<UseQueryOptions<Org[], Error>, 'queryKey' | 'queryFn'> = {}
) => {
  const { getUserRole, getUserOrgId } = useRBACStore();
  const userRole = getUserRole();
  const userOrgId = getUserOrgId();

  return useQuery<Org[], Error>({
    queryKey: unifiedOrgKeys.rbac.schools(userRole, selectedDistrictId),
    queryFn: async () => {
      let schools: Org[] = [];

      switch (userRole) {
        case 'tm_admin':
          if (selectedDistrictId) {
            schools = await organizationService.searchSchoolOrgs({
              parentOrgId: selectedDistrictId,
              noChamps: true
            });
          } else {
            schools = await organizationService.searchSchoolOrgs({ noChamps: true });
          }
          break;

        case 'district_champion':
          if (selectedDistrictId === userOrgId) {
            schools = await organizationService.searchSchoolOrgs({
              parentOrgId: selectedDistrictId,
              noChamps: true
            });
          }
          break;

        case 'school_champion':
          if (userOrgId) {
            const school = await organizationService.getOrgById(userOrgId);
            schools = [school];
          }
          break;

        default:
          schools = [];
      }
      return schools;
    },
    enabled: !!userRole && options.enabled !== false,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    ...options,
  });
};